import React, { useState } from "react";
import "./LoginV2.css";
import PropTypes from "prop-types";
import ResetPasswordModal from "./ResetPasswordModal";
import NewRegistrationModal from "./NewRegistrationModal";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonIcon from "@mui/icons-material/Person";
import PaymentModal from "@features/settings/modals/PaymentModal";
import OtpChallengeForm from "./OtpChallengeForm";
import { captureReferralCode } from "@/services/referralApi";

const API_URL = process.env.REACT_APP_SOPHYAPP_API_URL;

// Patient tab fallback when the brand has no portal of its own: dev/staging
// hosts use the env var.
const DEFAULT_PATIENT_PORTAL_URL =
  process.env.REACT_APP_PATIENT_PORTAL_URL || "https://portal.sophyapp.com";

async function loginUser(credentials) {
  const params = {
    account: {
      email: credentials.username.toLowerCase(),
      password: credentials.password,
    },
  };
  return fetch(
    process.env.REACT_APP_SOPHYAPP_API_URL + "/de/v2/account/login",
    {
      crossDomain: true,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    }
  ).then((data) => data.json());
}

async function loginPatient(credentials) {
  const params = {
    patient: {
      email: credentials.username,
      password: credentials.password,
    },
  };
  return fetch(API_URL + "/de/v2/patient/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  }).then((res) => res.json());
}

// Branding customers (medfit, MedAix, tenants via ?m=, …) get a bare login: no
// therapist sign-up (it starts a sophyapp trial), no sophyapp status page and
// footer. Patients go to the brand's own portal (medfit -> training.medfit.ch,
// tenants -> portal.sophyapp.com/?m=<id>).
export default function Login({
  setToken,
  setUserData,
  application,
  brandName,
  imagePath,
  isBrandingCustomer,
  patientPortalUrl,
  tenant,
}) {
  const TEMP_PAYMENT_TOKEN_KEY = "trial_payment_access_token";
  const PATIENT_PORTAL_URL = patientPortalUrl || DEFAULT_PATIENT_PORTAL_URL;
  const [activeTab, setActiveTab] = useState("therapist");

  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [loginFailedHeader, setLoginFailedHeader] = useState("");
  const [loginFailedBody, setLoginFailedBody] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAccessToken, setPaymentAccessToken] = useState(null);
  // Set once the password is accepted but the account has 2FA enabled.
  const [mfaToken, setMfaToken] = useState(null);

  // Patient login state
  const [patientUsername, setPatientUsername] = useState("");
  const [patientPassword, setPatientPassword] = useState("");
  const [patientShowPassword, setPatientShowPassword] = useState(false);
  const [patientIsLoading, setPatientIsLoading] = useState(false);
  const [patientLoginFailed, setPatientLoginFailed] = useState(false);

  const [modalShow, setModalShow] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordFailed, setPasswordFailed] = useState(false);

  // A referral link (?ref=SOPHY-XXXXX) goes straight to the registration.
  const [modalRegShow, setModalRegShow] = useState(
    () => !isBrandingCustomer && Boolean(captureReferralCode())
  );
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const [referralRejected, setReferralRejected] = useState(false);

  const { t } = useTranslation();

  // Tenants link their own privacy policy and imprint instead of hoferdigital's.
  const tenantFooterLinks = [
    { href: tenant?.privacy_url, label: t("general.dataPrivacy") },
    { href: tenant?.imprint_url, label: t("general.imprint", { defaultValue: "Impressum" }) },
  ].filter(({ href }) => href);

  const persistAccessToken = (accessToken) => {
    const now = Date.now();
    const expirationTime = 18000 * 1000;
    localStorage.setItem(
      "access_token",
      JSON.stringify({
        userToken: accessToken,
        expiration: now + expirationTime,
      })
    );
  };

  const persistSession = (token) => {
    setToken(token["access_token"]);
    setUserData(token);
    localStorage.setItem("userData", JSON.stringify(token));
    localStorage.removeItem("shownAnyPromoThisLogin");
    localStorage.setItem("showExercisePromo", "true");
  };

  const getErrorPayload = (responseData) => responseData?.error || responseData;

  const openTrialPaymentModal = (temporaryToken) => {
    if (!temporaryToken) {
      return;
    }
    setPaymentAccessToken(temporaryToken);
    localStorage.setItem(TEMP_PAYMENT_TOKEN_KEY, temporaryToken);
    persistAccessToken(temporaryToken);
    setShowPaymentModal(true);
  };

  const closeTrialPaymentModal = () => {
    setShowPaymentModal(false);

    const storedTempToken = localStorage.getItem(TEMP_PAYMENT_TOKEN_KEY);
    const storedAccessToken = localStorage.getItem("access_token");
    const parsedAccessToken = storedAccessToken ? JSON.parse(storedAccessToken) : null;

    if (
      storedTempToken &&
      parsedAccessToken?.userToken &&
      parsedAccessToken.userToken === storedTempToken
    ) {
      localStorage.removeItem("access_token");
    }

    localStorage.removeItem(TEMP_PAYMENT_TOKEN_KEY);
    setPaymentAccessToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = await loginUser({ username, password });
      if (token["access_token"]) {
        persistSession(token);
      } else {
        const errorPayload = getErrorPayload(token);
        const trialToken = errorPayload?.meta?.payment_access_token;

        if (errorPayload?.code === "OTP_REQUIRED") {
          // Password is correct but 2FA is enabled — switch to the OTP step.
          setMfaToken(errorPayload?.meta?.mfa_token || null);
          return;
        }

        if (errorPayload?.code === "TRIAL_EXPIRED") {
          setLoginFailedHeader(
            errorPayload.title ||
              t("payment.trialExpiredTitle", { defaultValue: "Trial expired" })
          );
          setLoginFailedBody(
            errorPayload.message ||
              t("payment.trialExpiredBody", {
                defaultValue:
                  "Your trial has expired. Please enter payment details to continue.",
              })
          );
          setLoginFailed(true);
          openTrialPaymentModal(trialToken);
        } else if (errorPayload?.title) {
          setLoginFailedHeader(errorPayload.title);
          setLoginFailedBody(errorPayload.message || t("user.check-form"));
        } else {
          setLoginFailedHeader(t("user.password-wrong"));
          setLoginFailedBody(t("user.check-form"));
        }
        setLoginFailed(true);
      }
    } catch {
      setLoginFailedHeader(t("user.password-wrong"));
      setLoginFailedBody(t("user.check-form"));
      setLoginFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setPatientIsLoading(true);
    setPatientLoginFailed(false);
    try {
      const result = await loginPatient({
        username: patientUsername,
        password: patientPassword,
      });
      if (result.access_token) {
        // The tenant's portal_url already carries ?m=, so build the query
        // instead of appending "?auth_token=".
        const target = new URL(PATIENT_PORTAL_URL);
        target.searchParams.set("auth_token", result.access_token);
        if (tenant) {
          target.searchParams.set("m", tenant.id);
        }
        window.location.href = target.toString();
      } else {
        setPatientLoginFailed(true);
      }
    } catch {
      setPatientLoginFailed(true);
    } finally {
      setPatientIsLoading(false);
    }
  };

  return (
    <div className="lv2-page">
      <div className="lv2-container">
        <div className="lv2-box">
          <img src={imagePath} alt={brandName || application} className="lv2-logo" />

          {/* Tabs – Therapeut (default) + Patient (redirects to portal) */}
          <div className="lv2-tabs">
            <button
              type="button"
              className={`lv2-tab${activeTab === "therapist" ? " active" : ""}`}
              onClick={() => setActiveTab("therapist")}
            >
              <ManageAccountsIcon className="lv2-tab-icon" />
              {t("user.therapist", { defaultValue: "Therapeut" })}
            </button>
            <button
              type="button"
              className={`lv2-tab${activeTab === "patient" ? " active" : ""}`}
              onClick={() => setActiveTab("patient")}
            >
              <PersonIcon className="lv2-tab-icon" />
              {t("user.patient", { defaultValue: "Patient" })}
            </button>
          </div>

          {/* ── 2FA challenge (after correct password when 2FA is enabled) ── */}
          {activeTab === "therapist" && mfaToken && (
            <OtpChallengeForm
              mfaToken={mfaToken}
              onVerified={persistSession}
              onBack={() => setMfaToken(null)}
            />
          )}

          {/* ── Therapist login form ── */}
          {activeTab === "therapist" && !mfaToken && (
            <div className="lv2-form-container">
              <form onSubmit={handleSubmit} noValidate>
                <div className="lv2-input-wrapper">
                  <input
                    type="email"
                    placeholder={t("user.user")}
                    className="lv2-input"
                    value={username}
                    onChange={(e) => setUserName(e.target.value.toLowerCase())}
                    autoFocus
                    required
                    autoComplete="username"
                  />
                </div>
                <div className="lv2-input-wrapper lv2-password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("user.password")}
                    className="lv2-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lv2-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <VisibilityOffIcon style={{ fontSize: "20px" }} />
                    )}
                  </button>
                </div>

                <div className="lv2-forgot-wrapper">
                  <span
                    className="lv2-forgot-link"
                    onClick={() => setModalShow(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setModalShow(true)}
                  >
                    {t("password.forgot")}
                  </span>
                </div>

                <div className="lv2-submit-wrapper">
                  <button
                    type="submit"
                    className="lv2-submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t("actions.loading", { defaultValue: "Lädt..." })
                      : t("user.sign-in")}
                  </button>
                </div>

                {!isBrandingCustomer && (
                  <div className="lv2-register-wrapper">
                    <p className="lv2-register-text">
                      {t("user.no-account", { defaultValue: "Sie haben noch kein Konto?" })}
                    </p>
                    <span
                      className="lv2-register-link"
                      onClick={() => setModalRegShow(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && setModalRegShow(true)
                      }
                    >
                      {t("user.register-now", { defaultValue: "Jetzt Registrieren" })}
                    </span>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* ── Patient login form ── */}
          {activeTab === "patient" && (
            <div className="lv2-form-container">
              <form onSubmit={handlePatientSubmit} noValidate>
                <div className="lv2-input-wrapper">
                  <input
                    type="email"
                    placeholder={t("user.user")}
                    className="lv2-input"
                    value={patientUsername}
                    onChange={(e) =>
                      setPatientUsername(e.target.value.toLowerCase())
                    }
                    autoFocus
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="lv2-input-wrapper lv2-password-wrapper">
                  <input
                    type={patientShowPassword ? "text" : "password"}
                    placeholder={t("user.password")}
                    className="lv2-input"
                    value={patientPassword}
                    onChange={(e) => setPatientPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lv2-password-toggle"
                    onClick={() => setPatientShowPassword(!patientShowPassword)}
                    tabIndex="-1"
                    aria-label={
                      patientShowPassword ? "Hide password" : "Show password"
                    }
                  >
                    {patientShowPassword ? (
                      <VisibilityIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <VisibilityOffIcon style={{ fontSize: "20px" }} />
                    )}
                  </button>
                </div>

                <div className="lv2-submit-wrapper">
                  <button
                    type="submit"
                    className="lv2-submit-btn"
                    disabled={patientIsLoading}
                  >
                    {patientIsLoading
                      ? t("actions.loading", { defaultValue: "Lädt..." })
                      : t("user.sign-in")}
                  </button>
                </div>

                <div className="lv2-register-wrapper">
                  <p className="lv2-register-text">
                    {t("user.no-account-patient")}
                  </p>
                  <a
                    href={PATIENT_PORTAL_URL}
                    className="lv2-register-link"
                    style={{ textDecoration: "none" }}
                  >
                    {t("user.register-now")}
                  </a>
                </div>
              </form>
            </div>
          )}

          {!isBrandingCustomer && (
            <div className="lv2-status-wrapper">
              <a
                href="https://sophyapp-status.statuspage.io"
                target="_blank"
                rel="noreferrer"
              >
                {t("user.status-link", {
                  defaultValue: "Probleme? Aktuelle Statusmeldungen hier.",
                })}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {!isBrandingCustomer && (
        <div className="lv2-footer">
          © hoferdigital gmbh &bull;{" "}
          <a
            href="https://www.hoferdigital.at/impressum-datenschutz/"
            target="_blank"
            rel="noreferrer"
          >
            {t("general.dataPrivacy")}
          </a>{" "}
          &bull;{" "}
          <a
            href="https://www.hoferdigital.at/impressum-datenschutz/"
            target="_blank"
            rel="noreferrer"
          >
            {t("general.terms")}
          </a>{" "}
          &bull;{" "}
          <a
            href="https://hoferdigital.atlassian.net/wiki/spaces/SS/pages/2949775361/therapeut.sophyapp.com+-+Wiki+f+r+Therapeuten"
            target="_blank"
            rel="noreferrer"
          >
            {t("general.support")}
          </a>
        </div>
      )}
      {isBrandingCustomer && tenantFooterLinks.length > 0 && (
        <div className="lv2-footer">
          {tenantFooterLinks.map(({ href, label }, index) => (
            <React.Fragment key={href + label}>
              {index > 0 && <> &bull; </>}
              <a href={href} target="_blank" rel="noreferrer">
                {label}
              </a>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Alerts */}
      <Alert
        variant="danger"
        className="lv2-alert"
        show={loginFailed}
        onClose={() => setLoginFailed(false)}
        dismissible
      >
        <Alert.Heading>
          {loginFailedHeader || t("user.password-wrong")}
        </Alert.Heading>
        <p>{loginFailedBody || t("user.check-form")}</p>
      </Alert>
      <Alert
        variant="success"
        className="lv2-alert"
        show={passwordSuccess}
        onClose={() => setPasswordSuccess(false)}
        dismissible
      >
        <Alert.Heading>{t("general.resetPassword")}</Alert.Heading>
        <p>{t("general.checkPassword")}</p>
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={passwordFailed}
        onClose={() => setPasswordFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("general.resetPassword")}</Alert.Heading>
        <p>{t("general.mailCorrect")}</p>
      </Alert>

      {/* Patient login alert */}
      <Alert
        variant="danger"
        className="lv2-alert"
        show={patientLoginFailed}
        onClose={() => setPatientLoginFailed(false)}
        dismissible
      >
        <Alert.Heading>{t("user.password-wrong")}</Alert.Heading>
        <p>{t("user.check-form")}</p>
      </Alert>

      {/* Registration alerts */}
      <Alert
        variant="success"
        className="lv2-alert"
        show={registrationSuccess}
        onClose={() => setRegistrationSuccess(false)}
        dismissible
      >
        <Alert.Heading>
          {t("user.must-registrate", { defaultValue: "Registrierung erfolgreich!" })}
        </Alert.Heading>
        <p>{t("user.check-mail", { defaultValue: "Bitte prüfe deine E-Mails." })}</p>
        {referralRejected && <p>{t("referral.registrationRejected")}</p>}
      </Alert>
      <Alert
        variant="danger"
        className="lv2-alert"
        show={registrationFailed}
        onClose={() => setRegistrationFailed(false)}
        dismissible
      >
        <Alert.Heading>
          {t("user.no-registration", { defaultValue: "Registrierung fehlgeschlagen." })}
        </Alert.Heading>
        <p>{t("user.already-registered", { defaultValue: "Diese E-Mail ist bereits registriert." })}</p>
      </Alert>

      {/* Modals */}
      {modalShow && (
        <ResetPasswordModal
          show={modalShow}
          setPasswordSuccess={setPasswordSuccess}
          setPasswordFailed={setPasswordFailed}
          application={application}
          onHide={() => setModalShow(false)}
        />
      )}
      {modalRegShow && (
        <NewRegistrationModal
          show={modalRegShow}
          setRegistrationSuccess={setRegistrationSuccess}
          setRegistrationFailed={setRegistrationFailed}
          setReferralRejected={setReferralRejected}
          onHide={() => setModalRegShow(false)}
        />
      )}
      {showPaymentModal && paymentAccessToken && (
        <PaymentModal show={showPaymentModal} onClose={closeTrialPaymentModal} />
      )}
    </div>
  );
}

Login.propTypes = {
  setToken: PropTypes.func.isRequired,
  setUserData: PropTypes.func.isRequired,
  application: PropTypes.string,
  brandName: PropTypes.string,
  imagePath: PropTypes.string,
  isBrandingCustomer: PropTypes.bool,
  patientPortalUrl: PropTypes.string,
  tenant: PropTypes.shape({
    id: PropTypes.number,
    privacy_url: PropTypes.string,
    imprint_url: PropTypes.string,
  }),
};
